import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const ProfileTagsSettings = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#FCD34D'
  });

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      const response = await api.get('/profile-tags');
      if (response.data.success) {
        setTags(response.data.tags);
      }
    } catch (error) {
      console.error('Erreur chargement tags:', error);
      toast.error('Erreur lors du chargement des tags');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      return toast.error('Le nom du tag est requis');
    }

    try {
      if (editingTag) {
        await api.put(`/profile-tags/${editingTag.id}`, formData);
        toast.success('Tag modifié avec succès');
      } else {
        await api.post('/profile-tags', formData);
        toast.success('Tag créé avec succès');
      }
      
      loadTags();
      handleCloseModal();
    } catch (error) {
      console.error('Erreur sauvegarde tag:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce tag ?')) {
      return;
    }

    try {
      await api.delete(`/profile-tags/${id}`);
      toast.success('Tag supprimé avec succès');
      loadTags();
    } catch (error) {
      console.error('Erreur suppression tag:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTag(null);
    setFormData({
      name: '',
      color: '#FCD34D'
    });
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>Gestion des Tags de Profil - Admin SunServ</title>
      </Helmet>

      <div className="p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Tags de Profil</h1>
            <p className="text-gray-400">
              Gérez les tags attribuables aux contributeurs (Développeur, Rédacteur, etc.)
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2"
          >
            <i className="fas fa-plus"></i>
            Nouveau Tag
          </button>
        </div>

        {/* Liste des tags */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4">Chargement des tags...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tags.map(tag => (
              <div
                key={tag.id}
                className="bg-surface border border-gray-700/30 rounded-xl p-6 hover:border-yellow-400/30 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: tag.color }}
                    >
                      <i className="fas fa-tag text-gray-900"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{tag.name}</h3>
                      <p className="text-sm text-gray-400">{tag.color}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(tag)}
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    <i className="fas fa-edit mr-2"></i>
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(tag.id)}
                    className="bg-red-500/10 hover:bg-red-500/20 text-red-400 px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}

            {tags.length === 0 && (
              <div className="col-span-full text-center py-12 bg-surface rounded-xl border border-gray-700/30">
                <i className="fas fa-tags text-5xl text-gray-600 mb-4"></i>
                <p className="text-gray-400">Aucun tag pour le moment</p>
                <button
                  onClick={() => setShowModal(true)}
                  className="mt-4 text-yellow-400 hover:text-yellow-300 transition-colors"
                >
                  Créer le premier tag
                </button>
              </div>
            )}
          </div>
        )}

        {/* Modal Créer/Modifier */}
        {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl p-8 max-w-md w-full border border-gray-700/30">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingTag ? 'Modifier le Tag' : 'Nouveau Tag'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nom du Tag
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-background border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                  placeholder="Ex: Développeur"
                  required
                />
              </div>

              {/* Couleur */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Couleur
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-16 h-12 rounded-lg cursor-pointer border-2 border-gray-700"
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="flex-1 px-4 py-3 bg-background border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    placeholder="#FCD34D"
                  />
                </div>
              </div>

              {/* Aperçu */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Aperçu
                </label>
                <div className="bg-background rounded-xl p-4 flex items-center justify-center">
                  <span
                    className="px-4 py-2 rounded-full text-sm font-medium text-gray-900"
                    style={{ backgroundColor: formData.color }}
                  >
                    {formData.name || 'Nom du tag'}
                  </span>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-xl font-semibold transition-colors"
                >
                  {editingTag ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default ProfileTagsSettings;
